import { Card, Table } from 'nhsuk-react-components';
import { Link } from 'react-router-dom';
import NoHoverTable from '../NoHoverTable';
import {
  conditionTargetToLabel,
  formatTeamId,
  unixTimestampToHumanReadableDate, unixTimestampToHumanReadableTime, YYYYMMDDToHumanReadable,
} from '../../utils/formattingUtils';
import { CampaignDetails } from '../../schemas/CampaignsList.schema';
import useFetchTeamDetails from '../../queries/fetch/fetchTeamDetails.ts';
import ProgressIndicator from '../ProgressIndicator';

interface CampaignsTableProps {
    campaigns: CampaignDetails[];
    teamId: string;
    showTeamId: boolean;
}

function CampaignsTable({
  campaigns, teamId, showTeamId,
}: CampaignsTableProps) {
  const teamDetails = useFetchTeamDetails({ teamId });
  const teamName = teamDetails.isLoading
    ? <ProgressIndicator text="Loading team details..." />
    : (teamDetails.data?.team?.TEAM_NAME ?? formatTeamId(teamId));

  return (
    <div>
      {campaigns.length === 0 ? (
        <Card>
          <>
            {showTeamId && (
            <h3>
              {teamName}
            </h3>
            )}
            <p>No campaigns found.</p>
          </>
        </Card>
      ) : (
        <Card>
          <>
            {showTeamId && (
              <h3>
                {teamName}
              </h3>
            )}
            <NoHoverTable>
              <Table.Head>
                <Table.Row>
                  <Table.Cell scope="col">Campaign</Table.Cell>
                  <Table.Cell scope="col" className="nhsuk-table__cell--numeric">Start/end date</Table.Cell>
                  <Table.Cell scope="col" className="nhsuk-table__cell--numeric">Last updated by</Table.Cell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                <>
                  {campaigns
                    .sort((a, b) => b.last_modified_timestamp - a.last_modified_timestamp)
                    .map((campaign: CampaignDetails) => (
                      <Table.Row key={campaign.id}>
                        <Table.Cell>
                          <Link
                            to={`/${teamId}/campaign/${campaign.id}/summary`}
                            aria-label={`View details for campaign ${campaign.name}`}
                            role="link"
                          >
                            {campaign.name}
                          </Link>
                          <div>{conditionTargetToLabel(campaign.target)}</div>
                        </Table.Cell>
                        <Table.Cell
                          className="nhsuk-table__cell--numeric"
                          aria-label={`Campaign starts on ${campaign.start_date} and ends on ${campaign.end_date}`}
                        >
                          <div className="no-wrap">
                            <div className="nhsuk-u-font-size-16">{YYYYMMDDToHumanReadable(campaign.start_date)}</div>
                            <div className="nhsuk-u-font-size-16">{YYYYMMDDToHumanReadable(campaign.end_date)}</div>
                          </div>
                        </Table.Cell>
                        <Table.Cell
                          className="nhsuk-table__cell--numeric"
                          aria-label={`Last updated on ${unixTimestampToHumanReadableDate(campaign.last_modified_timestamp)}`}
                        >
                          <div>
                            <div className="nhsuk-u-secondary-text-colour nhsuk-u-font-weight-bold nhsuk-u-font-size-16">{campaign.last_modified_by.name}</div>
                            <div className="nhsuk-u-font-size-16">{unixTimestampToHumanReadableDate(campaign.last_modified_timestamp)}</div>
                            <div className="nhsuk-u-font-size-16">{unixTimestampToHumanReadableTime(campaign.last_modified_timestamp)}</div>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                </>
              </Table.Body>
            </NoHoverTable>
          </>
        </Card>
      )}
    </div>
  );
}

export default CampaignsTable;
